import { useState } from "react";
import {
  Alert, Box, Button, MenuItem, Paper, Stack, TextField,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { purchaseRequisitionsApi, type PurchaseRequisitionListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { formatDate } from "@/shared/datetime";

const STATUS = ["", "Draft", "Submitted", "Approved", "Posted", "Cancelled"];

export function PurchaseRequisitionsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const query = useQuery({
    queryKey: ["purchase-requisitions", status, pagination.page, pagination.pageSize],
    queryFn: () => purchaseRequisitionsApi.list({
      status: status || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<PurchaseRequisitionListDto>[] = [
    { field: "documentNo", headerName: "Indent #", width: 180 },
    { field: "documentDate", headerName: "Date", width: 110,
      valueFormatter: (v) => v ? formatDate(v as string) : "" },
    { field: "neededByDate", headerName: "Needed by", width: 130,
      valueFormatter: (v) => v ? formatDate(v as string) : "—" },
    { field: "status", headerName: "Status", width: 140,
      renderCell: (p) => p.row.convertedToPurchaseOrderId
        ? <StatusPill label="Converted" tone="success" />
        : <StatusPill label={p.value as string} /> },
    { field: "lineCount", headerName: "Lines", width: 80, type: "number" },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Procurement", "Purchase indents"]}
        title="Purchase indents"
        actions={
          <Button variant="contained" onClick={() => navigate("/procurement/purchase-requisitions/new")}>
            New indent
          </Button>
        }
      />
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
            size="small" sx={{ minWidth: 160 }}>
            {STATUS.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            paginationMode="server" rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination} onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]} disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(p) => navigate(`/procurement/purchase-requisitions/${p.id}`)}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
